import React, { useContext } from 'react';
import { I18nContext } from 'translations';
import {
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import SaveIcon from '@material-ui/icons/Save';

const emptyContact = (contact_type) => ({
  contact_type,
  name: '',
  email: '',
  phone: '',
  _key: `${Date.now()}-${Math.random()}`,
});

const getContactKey = (contact) => contact.id || contact._key;

const ContactRow = ({ contact, onChange, onDelete, onSave, isEditMode, isLoading }) => {
  const { translate } = useContext(I18nContext);

  const handleChange = (field) => (e) =>
    onChange({ ...contact, [field]: e.target.value, _error: false, _dirty: true });

  const canSave = !contact.id || contact._dirty;

  return (
    <Grid container spacing={2} alignItems="flex-start">
      <Grid item xs={12} sm={4}>
        <TextField
          label={translate('name')}
          value={contact.name}
          onChange={handleChange('name')}
          fullWidth
          required
          size="small"
          variant="outlined"
          error={!!contact._error}
          helperText={contact._error ? translate('requiredField') : ''}
          disabled={isLoading}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label={translate('email')}
          value={contact.email || ''}
          onChange={handleChange('email')}
          fullWidth
          size="small"
          variant="outlined"
          type="email"
          disabled={isLoading}
        />
      </Grid>
      <Grid item xs={10} sm={isEditMode ? 2 : 3}>
        <TextField
          label={translate('phone')}
          value={contact.phone || ''}
          onChange={handleChange('phone')}
          fullWidth
          size="small"
          variant="outlined"
          disabled={isLoading}
        />
      </Grid>
      <Grid item xs={2} sm={1} style={{ paddingTop: '0.5rem', textAlign: 'center' }}>
        {isLoading ? (
          <CircularProgress size={20} style={{ margin: '0.4rem' }} />
        ) : (
          <>
            {isEditMode && (
              <IconButton
                onClick={() => onSave(contact)}
                size="small"
                color={canSave ? 'primary' : 'default'}
                disabled={!canSave}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton onClick={onDelete} size="small">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Grid>
    </Grid>
  );
};

const CompanyContacts = ({ contacts, onChange, onDelete, onSave, isEditMode, contactLoadingId }) => {
  const { translate } = useContext(I18nContext);

  const billingContacts = contacts.filter((c) => c.contact_type === 'billing');
  const portfolioContacts = contacts.filter((c) => c.contact_type === 'portfolio');

  const handleChange = (updatedContact) => {
    onChange(
      contacts.map((c) =>
        getContactKey(c) === getContactKey(updatedContact) ? updatedContact : c
      )
    );
  };

  const handleAdd = (contact_type) => {
    onChange([...contacts, emptyContact(contact_type)]);
  };

  const renderSection = (titleKey, sectionContacts, contact_type, addKey) => (
    <Grid container item xs={12} spacing={1}>
      <Grid item xs={12}>
        <Typography variant="subtitle2" color="textSecondary" style={{ fontWeight: 600 }}>
          {translate(titleKey)}
        </Typography>
      </Grid>
      {sectionContacts.map((contact) => (
        <Grid item xs={12} key={getContactKey(contact)}>
          <ContactRow
            contact={contact}
            onChange={handleChange}
            onDelete={() => onDelete(contact)}
            onSave={onSave}
            isEditMode={isEditMode}
            isLoading={contactLoadingId === getContactKey(contact)}
          />
        </Grid>
      ))}
      <Grid item xs={12}>
        <Button
          startIcon={<AddIcon />}
          onClick={() => handleAdd(contact_type)}
          size="small"
          color="primary"
          variant="text"
        >
          {translate(addKey)}
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
          {translate('app.setting.companyRegister.contacts.title')}
        </Typography>
      </Grid>
      {renderSection(
        'app.setting.companyRegister.contacts.billing.title',
        billingContacts,
        'billing',
        'app.setting.companyRegister.contacts.addBilling'
      )}
      <Grid item xs={12}>
        <Divider style={{ margin: '0.5rem 0' }} />
      </Grid>
      {renderSection(
        'app.setting.companyRegister.contacts.portfolio.title',
        portfolioContacts,
        'portfolio',
        'app.setting.companyRegister.contacts.addPortfolio'
      )}
    </Grid>
  );
};

export default CompanyContacts;
